import { Fragment } from 'react';
import type { ReactNode } from 'react';

type FlipDirection = 'horizontal' | 'vertical';

interface ParseResult {
  closed: boolean;
  index: number;
  nodes: ReactNode[];
}

type TextRenderer = (text: string) => ReactNode;

const OPENERS: Record<
  string,
  { closing: string; direction: FlipDirection }
> = {
  '{{': { closing: '}}', direction: 'horizontal' },
  '[[': { closing: ']]', direction: 'vertical' },
};

const renderTextChunk = (
  text: string,
  renderText: TextRenderer,
  key: string,
) => <Fragment key={key}>{renderText(text)}</Fragment>;

const parseSegment = (
  text: string,
  start: number,
  closing: string | undefined,
  renderText: TextRenderer,
  keyPrefix: string,
): ParseResult => {
  const nodes: ReactNode[] = [];
  let index = start;
  let chunkStart = start;
  let keyIndex = 0;

  const flushText = (end: number) => {
    if (end > chunkStart) {
      nodes.push(
        renderTextChunk(
          text.slice(chunkStart, end),
          renderText,
          `${keyPrefix}-text-${keyIndex++}`,
        ),
      );
    }
  };

  while (index < text.length) {
    if (closing && text.startsWith(closing, index)) {
      flushText(index);
      return { closed: true, index: index + closing.length, nodes };
    }

    const opener = text.slice(index, index + 2);
    const syntax = OPENERS[opener];

    if (!syntax) {
      index += 1;
      continue;
    }

    flushText(index);

    const nested = parseSegment(
      text,
      index + opener.length,
      syntax.closing,
      renderText,
      `${keyPrefix}-${keyIndex}`,
    );

    if (!nested.closed) {
      nodes.push(
        renderTextChunk(
          opener,
          renderText,
          `${keyPrefix}-text-${keyIndex++}`,
        ),
        ...nested.nodes,
      );
      return { closed: false, index: text.length, nodes };
    }

    nodes.push(
      <span
        className={`fujun-flip fujun-flip--${syntax.direction}`}
        key={`${keyPrefix}-flip-${keyIndex++}`}
      >
        {nested.nodes}
      </span>,
    );

    index = nested.index;
    chunkStart = index;
  }

  flushText(text.length);
  return { closed: false, index: text.length, nodes };
};

export const textToFujunFlipComponents = (
  text: string,
  renderText: TextRenderer,
): ReactNode[] => parseSegment(text, 0, undefined, renderText, 'fujun').nodes;
